import React from 'react';
import { withTranslation, WithTranslation } from 'react-i18next';

interface ErrorBoundaryProps extends WithTranslation {
    children: React.ReactNode;
    fallback?: React.ReactNode;
}

interface ErrorBoundaryState {
    hasError: boolean;
    error: Error | null;
}

/**
 * 错误边界组件
 * 用于捕获和处理React渲染错误
 */
export class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
    constructor(props: ErrorBoundaryProps) {
        super(props);
        this.state = { hasError: false, error: null };
    }

    static getDerivedStateFromError(error: Error): ErrorBoundaryState {
        // 更新状态，下一次渲染将显示回退UI
        return { hasError: true, error };
    }

    componentDidCatch(error: Error, errorInfo: React.ErrorInfo): void {
        // 记录错误信息
        console.error('[ErrorBoundary] Error caught:', error);
        console.error('[ErrorBoundary] Error info:', errorInfo);
        // 在这里可以集成Sentry, Highlight, Shakebugs等错误上报服务
        // Sentry.captureException(error, { extra: errorInfo });
    }

    render() {
        if (this.state.hasError) {
            if (this.props.fallback) {
                // 如果 fallback 是一个有效的 React 元素 (例如 <MyFallbackComponent />)
                // 我们克隆它并传递 error 对象作为 prop
                if (React.isValidElement(this.props.fallback)) {
                    return React.cloneElement(
                        this.props.fallback as React.ReactElement<{ error?: Error | null }>,
                        { error: this.state.error }
                    );
                }
                // 如果 fallback 不是一个可克隆的元素 (例如一个字符串或数字，虽然不常见)
                // 或者是一个函数组件的引用 (e.g. fallback={MyFallbackComponent})，这种方式不会直接传递props
                // 但由于我们在 App.tsx 中使用的是 <RootErrorBoundaryFallback /> 这样的元素，所以克隆是有效的
                return this.props.fallback;
            }

            // 默认的回退UI (如果 props.fallback 未提供)
            return (
                <div className="w-full h-screen flex flex-col items-center justify-center p-4">
                    <h2 className="text-xl font-bold mb-2">{this.props.t('common:errors.pageLoadingError')}</h2>
                    <p className="text-red-500 mb-4">
                        {this.state.error?.message || this.props.t('common:errors.unknownError')}
                    </p>
                    <button
                        className="px-4 py-2 bg-blue-500 text-white rounded"
                        onClick={() => window.location.href = '/'}
                    >
                        {this.props.t('common:actions.returnToHomepage')}
                    </button>
                </div>
            );
        }

        return this.props.children;
    }
}

export default withTranslation(['common'])(ErrorBoundary);
